"use client";

import { AmenityIcon } from "@/components/common/amenity-icon";
import { CloseBadgeIcon } from "@/components/common/close-badge-icon";
import { amenitiesInfo as amenitiesOptions, Amenity as ImportedAmenity } from "@/info/amenities";

import { cn } from "@/utils/cn";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import { Component, createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Control, Path, useController, useWatch } from "react-hook-form";
import { Button } from "../rac/button";

// Extend the imported Amenity interface with additional properties
interface Amenity extends ImportedAmenity {
	isSelected?: boolean;
	score?: number;
	emoji?: string;
	description?: string;
	tags?: string[];
	isCustom?: boolean;
	isPremium?: boolean;
}

interface Project {
	id: string;
	name: string;
	amenities?: string[] | { code: string }[];
}

type SearchMode = "contains" | "startsWith" | "fuzzy";
type LayoutMode = "grid" | "list" | "compact";
type SelectionMode = "single" | "multiple";

// Generic constraint for forms that have an amenities field
type FormWithAmenities = {
	amenities?: Amenity[] | string[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
};

interface AmenitiesIconGridProps<T extends FormWithAmenities = FormWithAmenities> {
	name?: keyof T;
	control: Control<T>;
	projectFieldName?: keyof T;
	label?: string;
	description?: string;
	placeholder?: string;
	required?: boolean;
	disabled?: boolean;
	className?: string;
	project?: Project;

	// Display options
	maxShown?: number;
	selectionMode?: SelectionMode;
	showCounter?: boolean;
	hideLabel?: boolean;

	// Search & filtering
	searchMode?: SearchMode;
	allowCustom?: boolean;
	customPrefix?: string;

	// Performance
	virtualizeThreshold?: number;
	debounceMs?: number;

	// Callbacks
	onSelectionChange?: (selected: Amenity[]) => void;
	onSearch?: (term: string) => void;
	onCustomCreate?: (name: string) => Amenity | Promise<Amenity>;

	// Styling
	variant?: "default" | "outlined" | "filled";
	size?: "sm" | "md" | "lg";

	// Hack for manual dirty tracking
	markFieldDirty?: (fieldName: string) => void;
}

interface AmenitiesContextValue {
	available: Amenity[];
	searchTerm: string;
	isLoading: boolean;
	error: string | null;
	addAmenity: (amenity: Amenity) => void;
	removeAmenity: (code: string) => void;
	setSearchTerm: (term: string) => void;
	clearSelection: () => void;
}

const AmenitiesContext = createContext<AmenitiesContextValue | null>(null);

const useAmenitiesContext = () => {
	const context = useContext(AmenitiesContext);
	if (!context) {
		throw new Error("useAmenitiesContext must be used within AmenitiesProvider");
	}
	return context;
};

/** Advanced debounce hook with cancel capability */
function useDebounce<T>(value: T, delay: number = 300) {
	const [debouncedValue, setDebouncedValue] = useState(value);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [value, delay]);

	const cancel = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	}, []);

	return [debouncedValue, cancel] as const;
}

/** Fuzzy search implementation */
function fuzzyMatch(text: string, query: string): number {
	const textLower = text.toLowerCase();
	const queryLower = query.toLowerCase();

	let score = 0;
	let queryIndex = 0;

	for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
		if (textLower[i] === queryLower[queryIndex]) {
			score += 1;
			queryIndex++;
		}
	}

	return queryIndex === queryLower.length ? score / text.length : 0;
}

/** Search amenities with different modes */
function searchAmenities(amenities: Amenity[], query: string, mode: SearchMode = "contains"): Amenity[] {
	if (!query.trim()) return amenities;

	const queryLower = query.toLowerCase().trim();

	switch (mode) {
		case "startsWith":
			return amenities.filter(
				a =>
					a.name.toLowerCase().startsWith(queryLower) ||
					(a.tags && a.tags.some(tag => tag.toLowerCase().startsWith(queryLower))),
			);

		case "fuzzy":
			return amenities
				.map(a => ({
					...a,
					score: Math.max(fuzzyMatch(a.name, query), ...(a.tags ? a.tags.map(tag => fuzzyMatch(tag, query)) : [0])),
				}))
				.filter(a => a.score > 0.3)
				.sort((a, b) => b.score - a.score);

		case "contains":
		default:
			return amenities.filter(a => {
				const nameMatch = a.name && a.name.toLowerCase().includes(queryLower);
				const descMatch = a.description && a.description.toLowerCase().includes(queryLower);
				const tagMatch = Array.isArray(a.tags) && a.tags.some(tag => tag && tag.toLowerCase().includes(queryLower));
				const catMatch = a.category && a.category.toLowerCase().includes(queryLower);
				return nameMatch || descMatch || tagMatch || catMatch;
			});
	}
}

/** Create custom amenity with validation */
function createCustomAmenity(name: string, existingCodes: Set<string>, prefix: string = "custom"): Amenity {
	const sanitizedName = name.trim();
	const baseCode = `${prefix}-${sanitizedName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;

	let code = baseCode;
	let counter = 1;

	while (existingCodes.has(code)) {
		code = `${baseCode}-${counter++}`;
	}

	return {
		code,
		name: sanitizedName,
		icon: "",
		emoji: "🏷️",
		order: 999,
		importance: 0,
		type: ["custom"],
		category: "Custom",
		tags: ["custom", "user-created"],
		isCustom: true,
		description: `Custom amenity: ${sanitizedName}`,
	};
}

function getProjectAmenities(projectId: string, project?: Project): string[] {
	if (projectId) return []; // should find amenities from the projectId
	if (project?.amenities?.length) {
		return project.amenities
			.map(amenity => {
				if (typeof amenity === "string") return amenity;
				if (typeof amenity === "object" && amenity.code) return amenity.code;
				return "";
			})
			.filter(Boolean);
	}

	return [];
}

/** Data fetching hook with caching */
function useAmenitiesData(projectId?: string, selectedCodes?: Set<string>) {
	const [isLoading] = useState(false); // setIsLoading removed (unused)
	const [error, setError] = useState<string | null>(null);

	const amenities = useMemo(() => {
		try {
			setError(null);
			if (selectedCodes) {
				return amenitiesOptions.map(amenity => {
					if (selectedCodes.has(amenity.code)) return { ...amenity, isSelected: true };
					return amenity;
				});
			}

			const pAmenities = projectId ? getProjectAmenities(projectId) : [];

			return amenitiesOptions.map(amenity => {
				if (pAmenities.includes(amenity.code)) return { ...amenity, isSelected: true };
				return amenity;
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load amenities");
			return amenitiesOptions;
		}
	}, [projectId, selectedCodes]);

	return { amenities, isLoading, error };
}

/** Error Boundary for the component */
class AmenitiesErrorBoundary extends Component<
	{ children: React.ReactNode; fallback?: React.ReactNode },
	{ hasError: boolean; error?: Error }
> {
	constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error };
	}

	render() {
		if (this.state.hasError) {
			return (
				this.props.fallback || (
					<div className="p-4 border border-primary-200 rounded-lg bg-primary-50">
						<p className="text-primary-600 text-sm">Something went wrong loading amenities. Please try again.</p>
					</div>
				)
			);
		}

		return this.props.children;
	}
}

/** Individual amenity chip with enhanced features */
interface ChipProps {
	amenity: Amenity;
	onRemove: (code: string) => void;
	variant?: "default" | "compact";
	disabled?: boolean;
}

const Chip = memo<ChipProps>(({ amenity, onRemove, variant = "default", disabled = false }) => {
	const handleRemove = useCallback(() => {
		if (!disabled) {
			onRemove(amenity.code);
		}
	}, [amenity.code, onRemove, disabled]);

	const chipClasses = cn("inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm transition-all duration-200", {
		"bg-white border border-primary-600 text-primary-600 hover:bg-primary-200": variant === "default",
		"bg-primary-200 text-primary-600 px-2 py-0.5 text-xs": variant === "compact",
		"opacity-50 cursor-not-allowed": disabled,
		"hover:shadow-sm": !disabled,
	});

	return (
		<div className={cn(chipClasses, "relative group")} title={amenity.description}>
			{amenity.emoji ? <span className="text-sm">{amenity.emoji}</span> : <AmenityIcon code={amenity.code} />}
			<span className="text-body">{amenity.name}</span>
			{amenity.isPremium && <span className="text-xs bg-amber-100 text-amber-600 px-1 rounded">Premium</span>}
			{!disabled && (
				<button
					type="button"
					onClick={handleRemove}
					className="absolute -top-2 -right-2 p-0.5 bg-white rounded-full border border-gray-300 shadow hover:bg-gray-100 transition-colors flex items-center justify-center w-5 h-5 opacity-0 group-hover:opacity-100 focus:opacity-100"
					aria-label={`Remove ${amenity.name}`}
				>
					<CloseBadgeIcon className="w-3 h-3" />
				</button>
			)}
		</div>
	);
});

Chip.displayName = "AmenityChip";

/** Search input with enhanced features */
interface SearchInputProps {
	value: string;
	onChange: (value: string) => void;
	onClear: () => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
}

const SearchInput = memo<SearchInputProps>(
	({ value, onChange, onClear, placeholder = "Search amenities...", disabled = false, className }) => {
		return (
			<div className={cn("relative", className)}>
				<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
				<input
					type="text"
					value={value}
					onChange={e => onChange(e.target.value)}
					placeholder={placeholder}
					disabled={disabled}
					className={cn(
						"w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg",
						"focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
						"disabled:bg-gray-50 disabled:cursor-not-allowed",
						"placeholder-gray-400 text-sm",
					)}
				/>
				{value && (
					<button
						type="button"
						onClick={onClear}
						className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
						aria-label="Clear search"
					>
						<X className="w-4 h-4" />
					</button>
				)}
			</div>
		);
	},
);

SearchInput.displayName = "SearchInput";

/** Options list with virtualization support */
interface OptionsListProps {
	options: Amenity[];
	onSelect: (amenity: Amenity) => void;
	searchTerm: string;
	allowCustom: boolean;
	onCreateCustom?: (name: string) => void;
	virtualize?: boolean;
	maxHeight?: string;
}

const OptionsList = memo<OptionsListProps>(
	({ options, onSelect, searchTerm, allowCustom, onCreateCustom, maxHeight = "300px" }) => {
		const handleCustomCreate = useCallback(() => {
			if (onCreateCustom && searchTerm.trim()) {
				onCreateCustom(searchTerm.trim());
			}
		}, [onCreateCustom, searchTerm]);

		if (options.length === 0 && searchTerm.trim()) {
			return (
				<div className="p-4 text-center">
					<p className="text-gray-500 text-sm mb-2">No amenities found for {searchTerm}</p>
					{allowCustom && (
						<button
							type="button"
							onClick={handleCustomCreate}
							className="inline-flex items-center gap-2 px-3 py-1 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
						>
							<Plus className="w-4 h-4" />
							Create {searchTerm}
						</button>
					)}
				</div>
			);
		}

		return (
			<div className="py-1" style={{ maxHeight }}>
				{options.map(amenity => (
					<button
						key={amenity.code}
						type="button"
						onClick={() => onSelect(amenity)}
						className={cn(
							"w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none",
							"flex items-center gap-3 text-sm transition-colors",
						)}
					>
						{amenity.emoji ? <span className="text-lg">{amenity.emoji}</span> : <AmenityIcon code={amenity.code} />}
						<div className="flex-1 min-w-0">
							<div className="font-medium text-gray-900">{amenity.name}</div>
							{amenity.description && <div className="text-xs text-gray-500 truncate">{amenity.description}</div>}
						</div>
						{amenity.isCustom && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Custom</span>}
						{amenity.isPremium && (
							<span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded">Premium</span>
						)}
					</button>
				))}
			</div>
		);
	},
);

OptionsList.displayName = "OptionsList";

const AmenitiesIconGrid = <T extends FormWithAmenities = FormWithAmenities>({
	name = "amenities" as keyof T,
	control,
	projectFieldName = "project" as keyof T,
	label = "Amenities",
	description,
	placeholder = "Search amenities...",
	required = false,
	disabled = false,
	className,

	// Display options
	maxShown = 5,
	selectionMode = "multiple",
	showCounter = true,
	hideLabel = false,

	// Search & filtering
	searchMode = "contains",
	allowCustom = true,
	customPrefix = "custom",

	// Performance
	virtualizeThreshold = 100,
	debounceMs = 300,

	// Callbacks
	onSelectionChange,
	onSearch,
	onCustomCreate,

	// Data
	project,

	// Hack for manual dirty tracking
	markFieldDirty,
}: AmenitiesIconGridProps<T>) => {
	/* -- Form integration -------------------------------------------- */
	const { field, fieldState } = useController({
		name: name as Path<T>,
		control,
		rules: required ? { required: "Please select at least one amenity" } : undefined,
	});

	const selectedAmenities: Amenity[] = useMemo(
		() => (Array.isArray(field.value) ? field.value : []) as Amenity[],
		[field.value],
	);
	const selectedCodes = useMemo(() => new Set(selectedAmenities.map(a => a.code)), [selectedAmenities]);

	/* -- Project watching -------------------------------------------- */
	const watchedProject = useWatch({
		name: projectFieldName as Path<T>,
		control,
	}) as Project | undefined;

	const currentProject = project || watchedProject;

	/* -- Data fetching ----------------------------------------------- */
	const { amenities: globalAmenities, isLoading, error } = useAmenitiesData(currentProject?.id, selectedCodes);

	/* -- Pre-select project amenities -------------------------------- */
	const hasInitialized = useRef(false);
	const hasBeenCleared = useRef(false);

	useEffect(() => {
		if (
			!hasInitialized.current &&
			!hasBeenCleared.current &&
			currentProject &&
			globalAmenities.length > 0 &&
			selectedAmenities.length === 0
		) {
			const projectAmenityCodes = getProjectAmenities(currentProject.id, currentProject);

			if (projectAmenityCodes.length > 0) {
				const projectAmenities = globalAmenities.filter(amenity => projectAmenityCodes.includes(amenity.code));

				if (projectAmenities.length > 0) {
					field.onChange(projectAmenities);
					onSelectionChange?.(projectAmenities);
				}
			}

			hasInitialized.current = true;
		}
	}, [currentProject, globalAmenities, selectedAmenities.length, field, onSelectionChange]);

	// Reset initialization when project changes
	useEffect(() => {
		hasInitialized.current = false;
		hasBeenCleared.current = false;
	}, [currentProject?.id]);

	/* -- Search state ------------------------------------------------ */
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearch] = useDebounce(searchInput, debounceMs);

	const filteredOptions = useMemo(() => {
		const filtered = searchAmenities(
			globalAmenities.filter(a => !selectedCodes.has(a.code)),
			debouncedSearch,
			searchMode,
		);
		return virtualizeThreshold && filtered.length > virtualizeThreshold
			? filtered.slice(0, virtualizeThreshold)
			: filtered;
	}, [globalAmenities, debouncedSearch, searchMode, virtualizeThreshold, selectedCodes]);

	const visibleChips = useMemo(() => selectedAmenities.slice(0, maxShown), [selectedAmenities, maxShown]);
	const hiddenChipsCount = Math.max(0, selectedAmenities.length - maxShown);

	const addAmenity = useCallback(
		(amenity: Amenity) => {
			if (selectedCodes.has(amenity.code) || disabled) return;

			const newSelected = selectionMode === "single" ? [amenity] : [...selectedAmenities, amenity];
			field.onChange(newSelected);
			// Hack: manually mark field as dirty since React Hook Form isn't working
			if (markFieldDirty && name) {
				markFieldDirty(String(name));
			}
			onSelectionChange?.(newSelected);
		},
		[field, selectedAmenities, selectedCodes, disabled, selectionMode, onSelectionChange, markFieldDirty, name],
	);

	const removeAmenity = useCallback(
		(code: string) => {
			if (disabled) return;

			const newSelected = selectedAmenities.filter(a => a.code !== code);
			field.onChange(newSelected);
			// Hack: manually mark field as dirty since React Hook Form isn't working
			if (markFieldDirty && name) {
				markFieldDirty(String(name));
			}
			onSelectionChange?.(newSelected);
		},
		[field, selectedAmenities, disabled, onSelectionChange, markFieldDirty, name],
	);

	const handleSearch = useCallback(
		(term: string) => {
			setSearchInput(term);
			onSearch?.(term);
		},
		[onSearch],
	);

	const handleCustomCreate = useCallback(
		(name: string) => {
			if (!allowCustom || disabled) return;

			const customAmenity = onCustomCreate
				? onCustomCreate(name)
				: createCustomAmenity(name, selectedCodes, customPrefix);

			if (customAmenity instanceof Promise) {
				customAmenity.then(addAmenity);
			} else {
				addAmenity(customAmenity);
			}

			setSearchInput("");
		},
		[allowCustom, disabled, onCustomCreate, selectedCodes, customPrefix, addAmenity],
	);

	const clearSelection = useCallback(() => {
		if (disabled || selectedAmenities.length === 0) return;
		hasBeenCleared.current = true;
		field.onChange([]);
		onSelectionChange?.([]);
	}, [field, disabled, onSelectionChange, selectedAmenities.length]);

	const contextValue: AmenitiesContextValue = useMemo(
		() => ({
			selectedAmenities,
			available: globalAmenities.filter(a => !selectedCodes.has(a.code)),
			searchTerm: debouncedSearch,
			isLoading,
			error,
			addAmenity,
			removeAmenity,
			setSearchTerm: handleSearch,
			clearSelection,
		}),
		[
			selectedAmenities,
			globalAmenities,
			selectedCodes,
			debouncedSearch,
			isLoading,
			error,
			addAmenity,
			removeAmenity,
			handleSearch,
			clearSelection,
		],
	);

	if (error) {
		return (
			<div className={cn("flex flex-col gap-2", className)}>
				{label && <label className="text-sm font-medium text-gray-900">{label}</label>}
				<div className="p-4 border border-primary-200 rounded-lg bg-primary-50">
					<p className="text-primary-600 text-sm">Error: {error}</p>
				</div>
			</div>
		);
	}

	return (
		<AmenitiesErrorBoundary>
			<AmenitiesContext.Provider value={contextValue}>
				<div className={cn("flex flex-col gap-3", className)}>
					{/* Header */}
					{!hideLabel && (
						<label className="text-title-medium">
							{label}
							{required && <span className="text-primary-500 ml-1">*</span>}
						</label>
					)}

					{/* Selected chips */}
					{selectedAmenities.length > 0 && (
						<div className="relative flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
							{visibleChips.map(amenity => (
								<Chip key={amenity.code} amenity={amenity} onRemove={removeAmenity} disabled={disabled} />
							))}

							{hiddenChipsCount > 0 && (
								<Popover.Root>
									<Popover.Trigger asChild>
										<button
											type="button"
											className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-primary-600 text-primary-600 rounded-full text-sm hover:bg-primary-200 transition-colors shadow group"
											disabled={disabled}
										>
											<span className="text-body">+{hiddenChipsCount} more</span>
											<ChevronDown className="w-3 h-3 ml-1" />
										</button>
									</Popover.Trigger>
									<Popover.Content
										side="bottom"
										align="start"
										className="max-w-sm max-h-64 overflow-y-auto scrollbar-thin rounded-lg border bg-white p-4 shadow-lg z-50"
									>
										<div className="flex flex-wrap gap-2">
											{selectedAmenities.map(amenity => (
												<Chip
													key={amenity.code}
													amenity={amenity}
													onRemove={removeAmenity}
													variant="compact"
													disabled={disabled}
												/>
											))}
										</div>
										<Popover.Arrow className="fill-white" />
									</Popover.Content>
								</Popover.Root>
							)}

							{selectedAmenities.length > 1 && !disabled && (
								<div className="w-full flex justify-end items-end">
									<Button variant="link" size="sm" onClick={clearSelection}>
										<X className="w-4 h-4" /> Clear all
									</Button>
								</div>
							)}
						</div>
					)}

					{/* Search and options */}
					{isLoading ? (
						<div className="p-8 text-center">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
							<p className="text-sm text-gray-500">Loading amenities...</p>
						</div>
					) : (
						<Popover.Root>
							<Popover.Trigger asChild>
								<div>
									<SearchInput
										value={searchInput}
										onChange={handleSearch}
										onClear={() => setSearchInput("")}
										placeholder={placeholder}
										disabled={disabled}
									/>
								</div>
							</Popover.Trigger>
							<Popover.Content
								side="bottom"
								align="start"
								className="w-[var(--radix-popover-trigger-width)] max-h-80 overflow-y-auto scrollbar-thin rounded-lg border bg-white shadow-lg z-50"
								onOpenAutoFocus={e => e.preventDefault()}
							>
								<OptionsList
									options={filteredOptions}
									onSelect={addAmenity}
									searchTerm={debouncedSearch}
									allowCustom={allowCustom}
									onCreateCustom={handleCustomCreate}
									// virtualize prop removed
									maxHeight="320px"
								/>
								<Popover.Arrow className="fill-white" />
							</Popover.Content>
						</Popover.Root>
					)}

					{/* Footer info */}
					<div className="flex items-center justify-between text-xs text-gray-500">
						{showCounter && (
							<span>
								{selectedAmenities.length} selected
								{globalAmenities.length > 0 && <> · {globalAmenities.length - selectedAmenities.length} available</>}
							</span>
						)}
					</div>

					{/* Description */}
					{description && <p className="text-body-small">{description}</p>}

					{/* Error message */}
					<p className="text-body-small-medium text-destructive h-1" role="alert">
						{fieldState.error?.message || ""}
					</p>
				</div>
			</AmenitiesContext.Provider>
		</AmenitiesErrorBoundary>
	);
};

export {
	AmenitiesContext,
	AmenitiesIconGrid,
	createCustomAmenity,
	fuzzyMatch,
	searchAmenities,
	useAmenitiesContext,
	type AmenitiesContextValue,
	type AmenitiesIconGridProps,
	type Amenity,
	type FormWithAmenities,
	type Amenity as ImportedAmenity,
	type LayoutMode,
	type SearchMode,
	type SelectionMode,
};
